const MAX_U32 = 4_294_967_295;
const TITLE_KINDS = new Set(["catch", "contest", "quest", "exchange"]);
// Current guide objects are detached snapshots; semantic revisions identify the
// same evidence across ordinary workspace count edits.
export function fishGuideProjectionKey(projection) {
  if (!projection || !Object.hasOwn(projection, "guideRevision")) return projection?.fishGuideProgress;
  return JSON.stringify([projection.guideRevision, projection.guideActiveRevision,
    projection.guide?.kind, projection.guide?.activeEvidenceId]);
}

function changedMembershipIds(previous, next) {
  const removed = new Set(previous || []);
  const changed = new Set();
  for (const id of next || []) {
    if (!removed.delete(id)) changed.add(id);
  }
  for (const id of removed) changed.add(id);
  return changed;
}

export function fishProgressChanges(previous, next) {
  if (previous?.kind !== "ready" || next?.kind !== "ready"
    || fishGuideProjectionKey(previous) !== fishGuideProjectionKey(next)
    || previous.showDried !== next.showDried) return null;
  const before = new Map((previous.catchCounts || []).map((entry) => [entry.itemId, entry]));
  const countFishIds = new Set();
  for (const entry of next.catchCounts || []) {
    const old = before.get(entry.itemId);
    if (!old || old.count !== entry.count || old.source !== entry.source) countFishIds.add(entry.itemId);
    before.delete(entry.itemId);
  }
  for (const id of before.keys()) countFishIds.add(id);
  return {
    countFishIds,
    favouriteFishIds: changedMembershipIds(previous.favouriteIds, next.favouriteIds),
    titleIds: changedMembershipIds(previous.obtainedTitleIds, next.obtainedTitleIds),
    titleVisibilityChanged: previous.showContestTitles !== next.showContestTitles
      || previous.showUnobtainableTitles !== next.showUnobtainableTitles,
  };
}


function positiveId(value, label) {
  if (!Number.isInteger(value) || value < 1 || value > MAX_U32) {
    throw new TypeError(`${label} must be a positive u32`);
  }
  return value;
}

export function requireCatchCount(value) {
  if (!Number.isInteger(value) || value < 0 || value > MAX_U32) {
    throw new TypeError("Fishydex catch count must be a u32");
  }
  return value;
}

export function titleIncluded(title, preferences) {
  return titleAvailabilityIncluded(title, preferences)
    && (title.kind !== "contest" || preferences.showContestTitles === true);
}

// Contest trophies remain visible on cards/details independently of totals.
export function titleAvailabilityIncluded(title, preferences) {
  return title.availability?.obtainable !== false || preferences.showUnobtainableTitles === true;
}

export function titleRequiredCount(title) {
  if (title.requirement != null) {
    return title.requirement.minimum > 0 ? title.requirement.minimum : null;
  }
  const average = title.reports?.average;
  if (!(title.reports?.count > 0) || !Number.isFinite(average) || average <= 0 || average > MAX_U32) return null;
  return Math.round(average) || null;
}

export function titleProgressIntent(title) {
  if (!title || typeof title !== "object" || Array.isArray(title) || !TITLE_KINDS.has(title.kind)) {
    throw new TypeError("Fishydex title must be a catalog title");
  }
  const titleId = positiveId(title.titleId, "Fishydex title ID");
  const itemId = title.itemId === null ? null : positiveId(title.itemId, "Fishydex title item ID");
  let minimum = titleRequiredCount(title);
  if (title.requirement !== null) {
    minimum = requireCatchCount(title.requirement?.minimum);
    const maximum = requireCatchCount(title.requirement?.maximum);
    if (minimum > maximum) throw new TypeError("Fishydex title requirement range is reversed");
  }
  return { titleId, itemId, kind: title.kind, minimum };
}

export function catchTitleIntents(itemId, titles = []) {
  if (!Array.isArray(titles) || titles.length > 4_096) {
    throw new TypeError("Fishydex titles must be a bounded array");
  }
  const seen = new Set();
  return titles.map(titleProgressIntent).filter((title) => {
    if (title.kind !== "catch") return false;
    if (title.itemId !== itemId || seen.has(title.titleId)) {
      throw new TypeError("Fishydex count titles must be distinct catch titles for this fish");
    }
    seen.add(title.titleId);
    return true;
  });
}

export function pruneCatchTitleOwnership(payload, titles) {
  if (titles.length === 0 || payload.obtainedTitleIds.length === 0) return payload;
  const ordinaryIds = new Set(titles.map((title) => title.titleId));
  payload.obtainedTitleIds = payload.obtainedTitleIds.filter((id) => !ordinaryIds.has(id));
  return payload;
}

export function writeCatchCount(payload, itemId, count, source = "manual") {
  const existing = payload.catchCounts.find((entry) => entry.itemId === itemId);
  if (existing) {
    existing.count = count;
    existing.source = source;
  } else {
    payload.catchCounts.push({ itemId, count, source });
  }
  return payload;
}

export function applyTitleOwnership(payload, title, owned, guideCount) {
  if (title.kind === "catch" && title.itemId !== null) {
    if (title.minimum === null || title.minimum === 0) {
      throw new TypeError("Fishydex catch title requires a known positive minimum");
    }
    const effective = projectTitleProgress({ itemId: title.itemId }, [], payload, guideCount).count;
    const baseline = effective.kind === "known" ? effective.count : 0;
    pruneCatchTitleOwnership(payload, [title]);
    return writeCatchCount(payload, title.itemId,
      owned ? Math.max(baseline, title.minimum) : Math.min(baseline, title.minimum - 1), "title");
  }
  const selected = new Set(payload.obtainedTitleIds);
  if (owned) selected.add(title.titleId);
  else selected.delete(title.titleId);
  payload.obtainedTitleIds = Array.from(selected);
  return payload;
}

// Imported Fish Guide evidence is read-only. An explicit editable count, even
// zero, replaces its count projection without altering its underlying snapshot.
export function projectTitleProgress(entry, titles, currentProjection, guideCount) {
  const itemId = entry?.item_id ?? entry?.itemId;
  const stored = currentProjection?.catchCounts?.find((value) => value.itemId === itemId);
  const count = stored
    ? { kind: "known", count: stored.count, source: stored.source }
    : guideCount?.kind === "known"
      ? { kind: "known", count: guideCount.count, source: "logger" }
      : guideCount ?? { kind: "unknown", count: null };
  const effective = count.kind === "known" ? count.count : 0;
  const ownedTitleIds = [];
  const segments = [];
  let previousMinimum = 0;
  for (const title of titles) {
    if (title.kind !== "catch" || title.itemId !== itemId) continue;
    const minimum = titleRequiredCount(title);
    const obtained = minimum !== null && effective >= minimum;
    const fill = count.kind !== "known" || minimum === null ? null
      : minimum > previousMinimum
        ? Math.max(0, Math.min(1, (effective - previousMinimum) / (minimum - previousMinimum)))
        : Number(obtained);
    if (obtained) {
      ownedTitleIds.push(title.titleId);
    }
    segments.push({ titleId: title.titleId, minimum, fill });
    if (minimum !== null) previousMinimum = Math.max(previousMinimum, minimum);
  }
  return { count, ownedTitleIds, segments };
}

export function projectTitleCaughtIds(fish, currentProjection, guideCountForEntry) {
  const caught = new Set((currentProjection?.catchCounts ?? [])
    .filter((entry) => entry.count > 0).map((entry) => entry.itemId));
  for (const entry of fish) {
    const count = projectTitleProgress(entry, [], currentProjection, guideCountForEntry(entry)).count;
    if (count.kind === "known" && count.count > 0) caught.add(entry.item_id ?? entry.itemId);
  }
  return Array.from(caught).sort((left, right) => left - right);
}

// Shared by Fishydex and map query projections. Fish with no included titles
// never match; associated catch ownership is derived only from capture counts.
export function createMissingTitlePredicate(titles, progress, guideCount) {
  const byFish = new Map();
  for (const title of titles) {
    if (title.itemId === null || !titleIncluded(title, progress)) continue;
    const rows = byFish.get(title.itemId) || [];
    rows.push(title);
    byFish.set(title.itemId, rows);
  }
  const owned = new Set(progress.obtainedTitleIds || []);
  return entry => {
    const available = byFish.get(entry.item_id ?? entry.itemId) || [];
    if (!available.length) return false;
    const catchOwned = new Set(projectTitleProgress(entry, available, progress, guideCount(entry)).ownedTitleIds);
    return available.some(title => !(title.kind === "catch" ? catchOwned : owned).has(title.titleId));
  };
}
